import React, { useState } from 'react'
import { motion as Motion, AnimatePresence } from 'framer-motion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

export default function Tab({ tabInfo, children, onTabChange }) {
  const [activeTab, setActiveTab] = useState(
    tabInfo.tabs.findIndex(tab => tab.current) !== -1 
      ? tabInfo.tabs.findIndex(tab => tab.current) 
      : 0
  )

  const {
    title = "Tab Component",
    description = "Tab component description",
    layout = "horizontal",
    textColor = "text-gray-900",
    textColorHover = "hover:text-gray-700",
    textColorActive = "text-blue-600",
    barTabActive = "bg-blue-500",
    bgColor = "bg-white",
    bgColorHover = "hover:bg-gray-50",
    bgColorActive = "bg-blue-50",
    borderColor = "border-gray-200",
    borderColorActive = "blue-500",
    tabs = []
  } = tabInfo

  const handleTabClick = (index) => {
    setActiveTab(index)
    // Llamar al callback si existe
    if (onTabChange) {
      onTabChange(index)
    }
  }

  const containerClass = layout === 'vertical' 
    ? 'flex flex-row h-full' 
    : 'flex flex-col h-full'

  const tabListClass = layout === 'vertical'
    ? `flex flex-col w-64 border-r ${borderColor} ${bgColor}`
    : `flex flex-row border-b ${borderColor} ${bgColor}`

  const tabButtonClass = (index) => {
    const isActive = index === activeTab
    const baseClass = `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all duration-200 md:border-l-0`
    
    if (layout === 'vertical') {
      return `${baseClass} w-full text-left justify-start ${
        isActive 
          ? `${textColorActive} ${bgColorActive} border-l-${borderColorActive}` 
          : `${textColor} ${textColorHover} ${bgColorHover} border-transparent`
      }`
    } else {
      return `${baseClass} border-l-0 border-b-2 ${
        isActive 
          ? `${textColorActive} ${bgColorActive} border-b-${borderColorActive}` 
          : `${textColor} ${textColorHover} ${bgColorHover} border-transparent`
      }`
    }
  }

  const contentClass = layout === 'vertical'
    ? 'flex-1 overflow-auto'
    : 'flex-1 overflow-auto'

  const tabVariants = {
    hidden: { opacity: 0, y: layout === 'horizontal' ? 10 : 0, x: layout === 'vertical' ? 10 : 0 },
    visible: { opacity: 1, y: 0, x: 0 },
    exit: { opacity: 0, y: layout === 'horizontal' ? -10 : 0, x: layout === 'vertical' ? -10 : 0 }
  }

  return (
    <div className="w-full h-full">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        <p className="text-sm text-gray-600">{description}</p>
      </div>

      {/* Tab Container */}
      <div className={containerClass}>
        {/* Tab Navigation */}
        <nav className={`${tabListClass} scrollbar-box thin overflow-x-auto pb-1 sm:pb-0`} role="tablist">
          {tabs.map((tab, index) => (
            <Motion.button
              key={index}
              onClick={() => handleTabClick(index)}
              className={`${tabButtonClass(index)}`}
              role="tab"
              aria-selected={index === activeTab}
              aria-controls={`tabpanel-${index}`}
              {...(tab.tourId ? { 'data-tour-id': tab.tourId } : {})}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.15 }}
            >
              {tab.icon && (
                <FontAwesomeIcon icon={tab.icon} className="text-lg" aria-hidden="true" />
              )}
              <span className="block w-max">{tab.name}</span>
              {index === activeTab && (
                <Motion.div
                  className={layout === 'vertical' 
                    ? `absolute right-0 w-1 h-full ${barTabActive}` 
                    : `absolute bottom-0 left-0 right-0 h-0.5 ${barTabActive}`
                  }
                  layoutId="activeTab"
                  transition={{ duration: 0.3 }}
                />
              )}
            </Motion.button>
          ))}
        </nav>

        {/* Tab Content */}
        <div className={contentClass}>
          <AnimatePresence mode="wait">
            <Motion.div
              key={activeTab}
              className={`h-full ${tabs[activeTab]?.contentClass || 'py-4'}`}
              role="tabpanel"
              id={`tabpanel-${activeTab}`}
              aria-labelledby={`tab-${activeTab}`}
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              {(() => {
                const childArray = React.Children.toArray(children);
                return childArray[activeTab] ? childArray[activeTab] : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <i className="fas fa-cube text-4xl mb-4 opacity-50" />
                    <p>Content for {tabs[activeTab]?.name}</p>
                    <p className="text-sm mt-2">Pass children components to display custom content</p>
                  </div>
                </div>
                );
              })()}
            </Motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
